import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, Truck, Search, ShoppingCart, MapPin, Mail } from "lucide-react";
import PublicLayout, { roleHome } from "@/components/public/PublicLayout";
import Seo from "@/components/public/Seo";
import { CategoryGrid, CtaBand, SectionHead, StepList } from "@/components/public/blocks";
import { CONTACT_EMAIL } from "@/components/public/categories";

function Hero() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 hero-grid opacity-30" aria-hidden="true" />
      <div className="absolute -top-24 end-0 w-[500px] h-[500px] bg-brand/20 rounded-full blur-[120px]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 pt-16 sm:pt-20 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fadeup">
          <p className="inline-flex px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs font-mono uppercase tracking-widest text-brand mb-6">{t("landing.badge")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.05] tracking-tight">
            {t("landing.title1")} {t("landing.title2")} <span className="text-brand">{t("landing.title3")}</span>
          </h1>
          <p className="text-slate-300 text-base md:text-lg mt-6 max-w-lg">{t("site.home.subtitle")}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="hero-cta-btn" onClick={() => navigate(user ? roleHome(user.role) : "/register")} size="lg" className="bg-brand hover:bg-brand-dark rounded-full h-12 px-7 text-base">
              {t("landing.startShopping")} <ArrowRight className="w-4 h-4 ms-1 rtl:rotate-180" aria-hidden="true" />
            </Button>
            <Link to="/sell"><Button data-testid="hero-merchant-btn" size="lg" variant="outline" className="rounded-full h-12 px-7 text-base bg-transparent border-white/20 text-white hover:bg-white/10">{t("landing.sell")}</Button></Link>
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-400">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" aria-hidden="true" /> {t("landing.secure")}</span>
            <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-brand" aria-hidden="true" /> {t("landing.dispatch")}</span>
          </div>
        </div>
        <div className="relative animate-fadeup" style={{ animationDelay: "0.15s" }}>
          <img src="/images/store-aisle.jpg" alt={t("site.home.heroAlt")} width="1200" height="823" fetchPriority="high"
            className="w-full aspect-[4/3] object-cover rounded-3xl border border-white/10" data-testid="hero-image" />
          <div className="absolute -bottom-5 start-5 rounded-2xl bg-slate-900/90 border border-white/10 px-5 py-4 backdrop-blur-md">
            <p className="text-xs font-mono uppercase tracking-widest text-brand">{t("site.home.heroCardLabel")}</p>
            <p className="font-display font-bold mt-1">{t("site.home.heroCardText")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowPreview() {
  const { t } = useTranslation();
  const steps = [
    { icon: Search, title: t("site.how.s1t"), body: t("site.how.s1b") },
    { icon: ShoppingCart, title: t("site.how.s2t"), body: t("site.how.s2b") },
    { icon: Truck, title: t("site.how.s3t"), body: t("site.how.s3b") },
    { icon: MapPin, title: t("site.how.s4t"), body: t("site.how.s4b") },
  ];
  return (
    <section className="max-w-7xl mx-auto px-5 py-20 border-t border-white/10" aria-labelledby="how-preview">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHead title={<span id="how-preview">{t("site.how.title")}</span>} subtitle={t("site.how.subtitle")} />
        <Link to="/how-it-works" data-testid="home-how-link" className="mb-10 text-brand font-semibold hover:underline underline-offset-4">{t("site.home.learnMore")}</Link>
      </div>
      <StepList steps={steps} />
    </section>
  );
}

function ContactStrip() {
  const { t } = useTranslation();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-20">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-display font-bold">{t("site.home.contactTitle")}</h2>
          <p className="text-slate-400 mt-2">{t("site.home.contactBody")}</p>
        </div>
        <a href={`mailto:${CONTACT_EMAIL}`} data-testid="home-contact-email" className="inline-flex items-center gap-2 text-brand font-semibold text-lg break-all hover:underline underline-offset-4">
          <Mail className="w-5 h-5 shrink-0" aria-hidden="true" /> {CONTACT_EMAIL}
        </a>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  return (
    <PublicLayout testId="landing-page">
      <Seo path="/" title={t("site.seo.homeTitle")} description={t("site.seo.homeDesc")} />
      <Hero />
      <section className="max-w-7xl mx-auto px-5 py-20 border-t border-white/10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHead title={t("site.home.catTitle")} subtitle={t("site.home.catSub")} />
          <Link to="/categories" data-testid="home-categories-link" className="mb-10 text-brand font-semibold hover:underline underline-offset-4">{t("site.home.viewAll")}</Link>
        </div>
        <CategoryGrid />
      </section>
      <HowPreview />
      <CtaBand title={t("site.home.sellTitle")} body={t("site.home.sellBody")} to="/sell" cta={t("site.home.sellCta")} testId="home-sell-cta" />
      <ContactStrip />
    </PublicLayout>
  );
}
